/**
 * The measurements of a page: its paper, its margins, its type size.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add an option, add a row to the table it belongs to and nothing else.**
 *
 * Each table below is the single definition of its setting. The type, the list
 * of ids, and the validation are all derived from it, so a new margin is one
 * line here plus its two names in the message catalogue, and every place that
 * offers margins picks it up without being touched.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Each table is a set of presets, not the set of permitted values. A
 * measurement can also be typed, held to the rule beside its table. The
 * presets remain the fast path and remain what most reports use: "22mm" is not
 * a decision most people want to make. But a house style that specifies 18mm
 * margins is not asking an opinion, and a builder that can only approximate it
 * produces a document that is visibly not the one that was approved.
 *
 * See `measure.ts` for what keeps a typed measurement safe, and for why the
 * rules matter more than the presets do.
 *
 * Pure. Measurements and names, no rendering.
 */

import type { MeasureRule } from '@/domain/layout/measure'

/**
 * The paper a report is printed on.
 *
 * In millimetres, both of them, including the one that is defined in inches.
 * The page is measured in one unit or the measurements cannot be compared,
 * and a custom paper typed in millimetres could not then be told apart from
 * Letter.
 */
export const PAGE_SIZES = {
  a4: { width: '210mm', height: '297mm' },
  letter: { width: '215.9mm', height: '279.4mm' },
} as const

/**
 * What a sheet may measure.
 *
 * The floor is a business card and the ceiling is A0. Neither is a paper this
 * is for; they are the bounds past which the value is not a page size at all.
 */
export const PAGE_RULE: MeasureRule = { unit: 'mm', min: 50, max: 1200, step: 0.1 }

/**
 * How much white space surrounds the text.
 *
 * Uneven top and bottom on purpose: a printed page wants more room at the foot
 * than the head, because a text block sitting slightly above centre looks
 * placed rather than dropped.
 */
export const MARGINS = {
  narrow: { top: '15mm', side: '15mm', bottom: '18mm' },
  normal: { top: '22mm', side: '20mm', bottom: '24mm' },
  wide: { top: '28mm', side: '30mm', bottom: '32mm' },
} as const

/**
 * What a margin may measure.
 *
 * Not zero. Every office printer has an unprintable strip at the edge, a
 * few millimetres wide and different on every model, so a margin below about
 * five millimetres is text that some printers will cut off and others will
 * not, which is worse than text that is reliably cut off. The ceiling stops a
 * margin from consuming the page it surrounds.
 */
export const MARGIN_RULE: MeasureRule = { unit: 'mm', min: 5, max: 100, step: 0.5 }

/**
 * The three measurements as one CSS shorthand.
 *
 * Held apart and joined here because two things need them separately: a
 * full-page section has to work out how much height the margins leave it, and
 * neither measurement can be read back out of a shorthand.
 */
export function marginShorthand(top: string, side: string, bottom: string): string {
  return `${top} ${side} ${bottom}`
}

/** Body text, in points, as a printed report is measured. */
export const TEXT_SIZES = {
  small: '9.5pt',
  normal: '10.5pt',
  large: '11.5pt',
} as const

/**
 * What body text may measure.
 *
 * Six points is the floor because it is roughly where printed text stops being
 * readable by people who need reading glasses, which is a good share of the
 * audience for an annual report. Twenty-four is past every house style and
 * into the range where somebody has typed a heading size into the body field.
 */
export const TEXT_SIZE_RULE: MeasureRule = { unit: 'pt', min: 6, max: 24, step: 0.5 }

export type PageSize = keyof typeof PAGE_SIZES
export type Margin = keyof typeof MARGINS
export type TextSize = keyof typeof TEXT_SIZES

/**
 * The ids, in the order they were written.
 *
 * Derived rather than listed a second time, so a table and its list can never
 * disagree about what exists.
 */
export const PAGE_SIZE_IDS = Object.keys(PAGE_SIZES) as readonly PageSize[]
export const MARGIN_IDS = Object.keys(MARGINS) as readonly Margin[]
export const TEXT_SIZE_IDS = Object.keys(TEXT_SIZES) as readonly TextSize[]

/**
 * A stored value, if it is one of the offered options.
 *
 * Used when reading a document, which may have been written by a version that
 * offered something this one does not.
 */
export function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}
